import { useMemo } from 'react';
import {
  ArrowDownToLine,
  ArrowUpFromLine,
  Building2,
  TrendingDown,
  TrendingUp,
  Users,
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/Card';
import { Skeleton } from '@/components/ui/Skeleton';
import { Badge } from '@/components/ui/Badge';
import { EmptyState } from '@/components/ui/EmptyState';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/Table';
import { MetricCard } from '@/components/financials/MetricCard';
import { cn } from '@/lib/utils';
import { formatCurrency, formatDate } from '@/lib/formatters';
import {
  useApAging,
  useArAging,
  type ApAgingRow,
  type ArAgingRow,
} from '@/features/financials/hooks';

/**
 * AR & AP tab on Main Financials — two stacked aging matrices powered by
 * the document-level QBO Invoice / Bill mirrors (migration 73 +
 * qbo-sync). Top half is receivables (`useArAging` → `v_ar_aging`),
 * bottom half is payables (`useApAging` → `v_ap_aging`).
 *
 * Layout follows QuickBooks' own A/R + A/P Aging Summary reports:
 *   counterparty | not due | 0-30 | 31-60 | 61-90 | 90+ | total open
 * with a totals row and a header MetricCard strip for the "what's the
 * total of all open AR vs all open AP" gut-check.
 *
 * Counterparties with zero open balance are absent from the views, so
 * the table only ever shows what's actually outstanding right now —
 * the same as flipping into QBO and asking "who owes us money / who do
 * we owe?".
 *
 * Mobile note: this page is on the X.5.1 backlog for full mobile
 * migration; until then the wide table relies on horizontal scroll
 * inside the Card. The aging columns wrap to two short lines on
 * narrow viewports via `whitespace-nowrap` per-header.
 */

/**
 * The five aging buckets, in display order. Defined once so the header,
 * footer, and per-row renderers all stay in lockstep — adding a new
 * bucket (e.g. "121+") only needs one entry here.
 */
const BUCKETS = [
  { key: 'not_due_cents',        label: 'Not due', variant: 'success',   dangerLevel: 0 },
  { key: 'bucket_0_30_cents',    label: '0 – 30',  variant: 'outline',   dangerLevel: 1 },
  { key: 'bucket_31_60_cents',   label: '31 – 60', variant: 'secondary', dangerLevel: 2 },
  { key: 'bucket_61_90_cents',   label: '61 – 90', variant: 'warning',   dangerLevel: 3 },
  { key: 'bucket_over_90_cents', label: '90+',     variant: 'warning',   dangerLevel: 4 },
] as const satisfies ReadonlyArray<{
  key: keyof ArAgingRow & keyof ApAgingRow;
  label: string;
  variant: 'success' | 'outline' | 'secondary' | 'warning';
  dangerLevel: 0 | 1 | 2 | 3 | 4;
}>;

type BucketKey = (typeof BUCKETS)[number]['key'];

export function ArApTab() {
  const arQuery = useArAging();
  const apQuery = useApAging();

  // The most recent successful fetch across either query. We surface
  // it once at the page header rather than per-card because both
  // datasets are refreshed by the same qbo-sync run on the backend.
  const lastFetchMs = Math.max(arQuery.dataUpdatedAt ?? 0, apQuery.dataUpdatedAt ?? 0);

  return (
    <div className="space-y-6">
      {lastFetchMs > 0 && (
        <p className="text-sm text-muted-foreground">
          Last refreshed{' '}
          <span className="font-medium text-foreground">
            {formatDate(new Date(lastFetchMs))}
          </span>
        </p>
      )}

      <ArAgingSection rows={arQuery.data ?? null} loading={arQuery.isLoading} />
      <ApAgingSection rows={apQuery.data ?? null} loading={apQuery.isLoading} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// AR (receivables) section
// ---------------------------------------------------------------------------

function ArAgingSection({
  rows,
  loading,
}: {
  rows: ArAgingRow[] | null;
  loading: boolean;
}) {
  const totals = useMemo(() => sumBuckets(rows ?? []), [rows]);
  const totalCount = (rows ?? []).reduce((acc, r) => acc + (r.invoice_count ?? 0), 0);

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <MetricCard
          label="Total receivables"
          value={formatCurrency(totals.total_open_cents)}
          icon={<ArrowDownToLine className="h-4 w-4 text-bright-moss-dark" />}
          loading={loading}
          hint={`${totalCount.toLocaleString()} open invoice${totalCount === 1 ? '' : 's'} across ${(rows ?? []).length.toLocaleString()} customer${(rows ?? []).length === 1 ? '' : 's'}`}
        />
        <MetricCard
          label="Past due"
          value={formatCurrency(totals.past_due_cents)}
          icon={<TrendingDown className="h-4 w-4 text-fresh-salmon-dark" />}
          loading={loading}
          hint={
            totals.total_open_cents > 0
              ? `${Math.round((totals.past_due_cents / totals.total_open_cents) * 100)}% of open AR`
              : 'No open receivables'
          }
        />
        <MetricCard
          label="Over 90 days"
          value={formatCurrency(totals.bucket_over_90_cents)}
          icon={<TrendingDown className="h-4 w-4 text-fresh-salmon-dark" />}
          loading={loading}
          hint={
            totals.total_open_cents > 0
              ? `${Math.round((totals.bucket_over_90_cents / totals.total_open_cents) * 100)}% of open AR`
              : '—'
          }
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-4 w-4 text-muted-foreground" />
            Receivables · A/R Aging
          </CardTitle>
        </CardHeader>
        <CardContent className="px-0">
          {loading ? (
            <div className="space-y-2 px-6">
              <Skeleton className="h-10" />
              <Skeleton className="h-10" />
              <Skeleton className="h-10" />
            </div>
          ) : (rows ?? []).length === 0 ? (
            <div className="px-6">
              <EmptyState
                icon={Users}
                title="No open receivables"
              />
            </div>
          ) : (
            <AgingMatrix
              counterpartyLabel="Customer"
              rows={(rows ?? []).map((r) => ({
                key: r.customer_id ?? r.qb_customer_id ?? r.customer_name ?? 'unknown',
                name: r.customer_name ?? 'Unknown customer',
                buckets: r,
                docCount: r.invoice_count,
                oldestDueDate: r.oldest_due_date,
                maxDaysPastDue: r.max_days_past_due,
              }))}
              totals={totals}
              docLabel="invoice"
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// AP (payables) section
// ---------------------------------------------------------------------------

function ApAgingSection({
  rows,
  loading,
}: {
  rows: ApAgingRow[] | null;
  loading: boolean;
}) {
  const totals = useMemo(() => sumBuckets(rows ?? []), [rows]);
  const totalCount = (rows ?? []).reduce((acc, r) => acc + (r.bill_count ?? 0), 0);

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <MetricCard
          label="Total payables"
          value={formatCurrency(totals.total_open_cents)}
          icon={<ArrowUpFromLine className="h-4 w-4 text-fresh-salmon-dark" />}
          loading={loading}
          hint={`${totalCount.toLocaleString()} open bill${totalCount === 1 ? '' : 's'} across ${(rows ?? []).length.toLocaleString()} vendor${(rows ?? []).length === 1 ? '' : 's'}`}
        />
        <MetricCard
          label="Past due"
          value={formatCurrency(totals.past_due_cents)}
          icon={<TrendingUp className="h-4 w-4 text-fresh-salmon-dark" />}
          loading={loading}
          hint={
            totals.total_open_cents > 0
              ? `${Math.round((totals.past_due_cents / totals.total_open_cents) * 100)}% of open AP`
              : 'No open payables'
          }
        />
        <MetricCard
          label="Over 90 days"
          value={formatCurrency(totals.bucket_over_90_cents)}
          icon={<TrendingUp className="h-4 w-4 text-fresh-salmon-dark" />}
          loading={loading}
          hint={
            totals.total_open_cents > 0
              ? `${Math.round((totals.bucket_over_90_cents / totals.total_open_cents) * 100)}% of open AP`
              : '—'
          }
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-4 w-4 text-muted-foreground" />
            Payables · A/P Aging
          </CardTitle>
        </CardHeader>
        <CardContent className="px-0">
          {loading ? (
            <div className="space-y-2 px-6">
              <Skeleton className="h-10" />
              <Skeleton className="h-10" />
              <Skeleton className="h-10" />
            </div>
          ) : (rows ?? []).length === 0 ? (
            <div className="px-6">
              <EmptyState
                icon={Building2}
                title="No open payables"
              />
            </div>
          ) : (
            <AgingMatrix
              counterpartyLabel="Vendor"
              rows={(rows ?? []).map((r) => ({
                key: r.vendor_id ?? r.qb_vendor_id ?? r.vendor_name ?? 'unknown',
                name: r.vendor_name ?? 'Unknown vendor',
                buckets: r,
                docCount: r.bill_count,
                oldestDueDate: r.oldest_due_date,
                maxDaysPastDue: r.max_days_past_due,
              }))}
              totals={totals}
              docLabel="bill"
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared matrix + totals helpers
// ---------------------------------------------------------------------------

interface BucketTotals {
  not_due_cents: number;
  bucket_0_30_cents: number;
  bucket_31_60_cents: number;
  bucket_61_90_cents: number;
  bucket_over_90_cents: number;
  total_open_cents: number;
  past_due_cents: number;
}

function sumBuckets(rows: Array<ArAgingRow | ApAgingRow>): BucketTotals {
  const totals: BucketTotals = {
    not_due_cents: 0,
    bucket_0_30_cents: 0,
    bucket_31_60_cents: 0,
    bucket_61_90_cents: 0,
    bucket_over_90_cents: 0,
    total_open_cents: 0,
    past_due_cents: 0,
  };
  for (const r of rows) {
    totals.not_due_cents += r.not_due_cents ?? 0;
    totals.bucket_0_30_cents += r.bucket_0_30_cents ?? 0;
    totals.bucket_31_60_cents += r.bucket_31_60_cents ?? 0;
    totals.bucket_61_90_cents += r.bucket_61_90_cents ?? 0;
    totals.bucket_over_90_cents += r.bucket_over_90_cents ?? 0;
    totals.total_open_cents += r.total_open_cents ?? 0;
  }
  totals.past_due_cents =
    totals.bucket_0_30_cents +
    totals.bucket_31_60_cents +
    totals.bucket_61_90_cents +
    totals.bucket_over_90_cents;
  return totals;
}

interface AgingMatrixRow {
  key: string;
  name: string;
  buckets: Record<BucketKey, number> & { total_open_cents: number };
  docCount: number;
  oldestDueDate: string | null;
  maxDaysPastDue: number | null;
}

function AgingMatrix({
  counterpartyLabel,
  rows,
  totals,
  docLabel,
}: {
  counterpartyLabel: string;
  rows: AgingMatrixRow[];
  totals: BucketTotals;
  docLabel: 'invoice' | 'bill';
}) {
  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[28%] min-w-[12rem]">{counterpartyLabel}</TableHead>
            {BUCKETS.map((b) => (
              <TableHead key={b.key} className="whitespace-nowrap text-right">
                {b.label}
              </TableHead>
            ))}
            <TableHead className="whitespace-nowrap text-right">Total open</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <AgingRow key={row.key} row={row} docLabel={docLabel} />
          ))}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TableCell className="font-medium">Total</TableCell>
            {BUCKETS.map((b) => (
              <TableCell
                key={b.key}
                className="text-right font-medium tabular-nums"
              >
                {totals[b.key] === 0 ? '—' : formatCurrency(totals[b.key])}
              </TableCell>
            ))}
            <TableCell className="text-right font-semibold tabular-nums">
              {formatCurrency(totals.total_open_cents)}
            </TableCell>
          </TableRow>
        </TableFooter>
      </Table>
    </div>
  );
}

function AgingRow({
  row,
  docLabel,
}: {
  row: AgingMatrixRow;
  docLabel: 'invoice' | 'bill';
}) {
  const worstBucket = BUCKETS.reduce(
    (worst, b) => (row.buckets[b.key] > 0 && b.dangerLevel > worst.dangerLevel ? b : worst),
    BUCKETS[0],
  );

  return (
    <TableRow>
      <TableCell>
        <div className="font-medium text-foreground">{row.name}</div>
        <div className="mt-0.5 flex items-center gap-1.5 text-xs text-muted-foreground">
          <span>
            {row.docCount} {docLabel}
            {row.docCount === 1 ? '' : 's'}
          </span>
          {row.oldestDueDate && (
            <>
              <span>·</span>
              <span>oldest due {formatDate(row.oldestDueDate)}</span>
            </>
          )}
          {worstBucket.dangerLevel > 0 && (
            <Badge variant={worstBucket.variant} className="ml-1">
              {worstBucket.label} days
            </Badge>
          )}
        </div>
      </TableCell>
      {BUCKETS.map((b) => {
        const cents = row.buckets[b.key];
        return (
          <TableCell
            key={b.key}
            className={cn(
              'text-right tabular-nums',
              cents === 0 && 'text-muted-foreground',
              cents > 0 && b.dangerLevel >= 3 && 'text-fresh-salmon-dark',
            )}
          >
            {cents === 0 ? '—' : formatCurrency(cents)}
          </TableCell>
        );
      })}
      <TableCell className="text-right font-medium tabular-nums">
        {formatCurrency(row.buckets.total_open_cents)}
      </TableCell>
    </TableRow>
  );
}
